/**
 * IntlInvoiceDetailModal — Revisión y contabilización de una invoice
 * internacional en staging.
 *
 * Columna izquierda: preview del PDF (iframe S3 firmado).
 * Columna derecha: metadata editable + supplier + botones de acción.
 */
import { tenantLocale } from '@/lib/locale'
import { useEffect, useMemo, useState } from 'react'

import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCcw, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import DatePicker from '@/components/DatePicker'
import CustomFieldsForm, { type Personalizado } from '@/components/CustomFieldsForm'
import { useToast } from '@/components/Toast'
import { useGlobalCustomFields } from '@/modules/config/hooks/useConfig'
import { usePermission } from '@/hooks/usePermission'
import { displayLabelCls, inputCls, labelCls } from '@/lib/formStyles'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { useSuppliers } from '@/modules/purchases/hooks/usePurchases'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

import {
  useDiscardIntlInvoice,
  useIntlInvoice,
  useIntlInvoicePdfUrl,
  usePostIntlInvoice,
  useReparseIntlInvoice,
  useUpdateIntlInvoice,
} from '../hooks/useIntlInvoices'


function fmtAmount(value: string | null | undefined, currency = 'USD'): string {
  if (!value) return '--'
  const num = parseFloat(value)
  if (isNaN(num)) return '--'
  try {
    return new Intl.NumberFormat(tenantLocale(), {
      style: 'currency', currency, maximumFractionDigits: currency === 'CLP' ? 0 : 2,
    }).format(num)
  } catch {
    return `${num.toFixed(2)} ${currency}`
  }
}


interface Props {
  id: string
  onClose: () => void
}

export function IntlInvoiceDetailModal({ id, onClose }: Props) {
  const { data: invoice, isLoading } = useIntlInvoice(id)
  const { data: pdfUrl } = useIntlInvoicePdfUrl(id)
  const { data: suppliersData } = useSuppliers({ page_size: 9999, active: 'true' })
  const update = useUpdateIntlInvoice()
  const post = usePostIntlInvoice()
  const discard = useDiscardIntlInvoice()
  const reparse = useReparseIntlInvoice()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const { can } = usePermission()
  const canEdit = can('intl_invoices', 'edit')
  const canApprove = can('intl_invoices', 'approve')
  const canDelete = can('intl_invoices', 'delete')

  const [error, setError] = useState<string | null>(null)
  const [localSupplier, setLocalSupplier] = useState<string>('')
  const [localInvoiceDate, setLocalInvoiceDate] = useState<string>('')
  const [localDueDate, setLocalDueDate] = useState<string>('')
  const [localNumber, setLocalNumber] = useState<string>('')
  const [localTotal, setLocalTotal] = useState<string>('')
  const [localAccount, setLocalAccount] = useState<string>('')
  const [localTargetStatus, setLocalTargetStatus] = useState<'approved' | 'registered'>('approved')
  const [personalizados, setPersonalizados] = useState<Personalizado[]>([])

  // Campos personalizados de compras (scope='compras').
  const { data: comprasFields = [] } = useGlobalCustomFields(true, 'compras')

  const { data: accountsData } = useAccounts({ is_active: 'true', is_imputable: 'true' })
  const accountItems = useMemo(() => {
    const list = accountsData?.accounts ?? []
    return list
      .filter(a => ['expense', 'asset'].includes(a.account_type))
      .map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))
  }, [accountsData])

  useEffect(() => {
    if (!invoice) return
    setLocalSupplier(invoice.supplier ?? '')
    setLocalInvoiceDate(invoice.invoice_date ?? '')
    setLocalDueDate(invoice.due_date ?? '')
    setLocalNumber(invoice.external_number)
    setLocalTotal(invoice.amount_total_original)
  }, [invoice])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const supplierItems = useMemo(() => {
    const list = suppliersData?.suppliers ?? []
    return list.map(s => ({
      id: s.id,
      label: `${s.legal_name}${s.tax_id ? ` (${s.tax_id})` : ''}`,
    }))
  }, [suppliersData])

  const readOnly = !invoice || invoice.status === 'posted' || invoice.status === 'discarded' || !canEdit

  const currentPayload = () => ({
    supplier: localSupplier || null,
    external_number: localNumber,
    invoice_date: localInvoiceDate || null,
    due_date: localDueDate || null,
    amount_total_original: localTotal,
  } as any)

  const handleSave = async () => {
    if (!invoice) return
    setError(null)
    try {
      await update.mutateAsync({ id: invoice.id, data: currentPayload() })
      toast.success('Cambios guardados.')
    } catch (exc: any) {
      setError(exc?.response?.data?.error?.message || 'Error al guardar.')
    }
  }

  const doPost = async () => {
    if (!invoice) return
    setError(null)
    try {
      if (
        localSupplier !== (invoice.supplier ?? '') ||
        localInvoiceDate !== (invoice.invoice_date ?? '') ||
        localDueDate !== (invoice.due_date ?? '') ||
        localNumber !== invoice.external_number ||
        localTotal !== invoice.amount_total_original
      ) {
        await update.mutateAsync({ id: invoice.id, data: currentPayload() })
      }
      const result = await post.mutateAsync({ id: invoice.id, accountId: localAccount || undefined, targetStatus: localTargetStatus, personalizados })
      toast.success(
        localTargetStatus === 'registered'
          ? `Importada en borrador: ${fmtAmount(result.total_clp, 'CLP')} (tasa ${result.exchange_rate}). Apruébala para contabilizar.`
          : `Contabilizada: ${fmtAmount(result.total_clp, 'CLP')} (tasa ${result.exchange_rate}).`,
      )
      onClose()
    } catch (exc: any) {
      setError(exc?.response?.data?.error?.message || 'Error al contabilizar.')
    }
  }

  const handlePost = () => {
    if (!invoice) return
    if (!localSupplier) {
      setError('Debes asignar un proveedor antes de contabilizar.')
      return
    }
    confirm({
      message: localTargetStatus === 'registered'
        ? 'Se creará una factura de proveedor en borrador (registrada, sin contabilizar). Se puede revisar y aprobar después para generar el comprobante.'
        : 'Se creará un comprobante contable en borrador y una factura de proveedor. Esta acción no se puede deshacer automáticamente.',
      variant: 'warning',
      confirmLabel: localTargetStatus === 'registered' ? 'Importar en borrador' : 'Contabilizar',
      onConfirm: doPost,
    })
  }

  const handleDiscard = () => {
    if (!invoice) return
    confirm({
      message: 'La invoice se eliminará definitivamente (no se podrá recuperar).',
      variant: 'danger',
      confirmLabel: 'Eliminar',
      onConfirm: async () => {
        try {
          await discard.mutateAsync(invoice.id)
          toast.success('Invoice eliminada.')
          onClose()
        } catch {
          toast.error('Error al eliminar.')
        }
      },
    })
  }

  const handleReparse = async () => {
    if (!invoice) return
    try {
      await reparse.mutateAsync(invoice.id)
      toast.success('Re-parseo encolado.')
    } catch {
      toast.error('Error al re-parsear.')
    }
  }

  return (
    <>
      <Modal open onClose={onClose} title={invoice?.external_number || 'Invoice internacional'} subtitle={invoice?.supplier_detail ? invoice.supplier_detail.legal_name : undefined} size="2xl">
        {isLoading || !invoice ? (
          <div className="p-12 text-center text-sm text-content-subtle">Cargando…</div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
            {/* PDF */}
            <div className="bg-surface-muted border-r border-divider min-h-0 overflow-hidden">
              {pdfUrl?.mode === 's3' && pdfUrl.url ? (
                <iframe title="PDF preview" src={pdfUrl.url} className="w-full h-full" />
              ) : pdfUrl?.mode === 'local' ? (
                <div className="p-8 text-center text-sm text-content-muted">
                  <p className="font-medium">Modo desarrollo (sin S3).</p>
                  <p className="mt-2 font-mono text-xs break-all">{pdfUrl.pdf_s3_key}</p>
                </div>
              ) : (
                <div className="p-8 text-center text-sm text-content-subtle">Cargando vista previa…</div>
              )}
            </div>

            {/* Form */}
            <div className="overflow-y-auto p-5 space-y-4">
              {invoice.parse_warnings.length > 0 && (
                <div className="bg-warning-surface border border-warning-border rounded-lg p-3 text-xs text-warning-content-strong">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={13} />
                    Advertencias del parser
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {invoice.parse_warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {invoice.parse_confidence && parseFloat(invoice.parse_confidence) < 0.7 && (
                <div className="bg-danger-surface border border-danger-border rounded-lg p-3 text-xs text-danger-content-strong flex items-center gap-2">
                  <AlertTriangle size={14} />
                  Confianza del parser baja ({invoice.parse_confidence}) — revisa todos los campos.
                </div>
              )}

              {invoice.duplicate_of && (
                <div className="bg-danger-surface border border-danger-border rounded-lg p-3 text-xs text-danger-content-strong">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={13} />
                    Documento duplicado
                  </div>
                  <p>
                    Ya existe una factura contabilizada para este proveedor con el
                    número <span className="font-mono">{invoice.external_number}</span>.
                    No se podrá contabilizar dos veces el mismo documento.
                  </p>
                  {invoice.duplicate_of.supplier_invoice_id && (
                    <Link
                      to={`/purchases/invoices?highlight=${invoice.duplicate_of.supplier_invoice_id}`}
                      onClick={onClose}
                      className="mt-1 inline-flex items-center gap-0.5 text-danger-content underline decoration-dotted hover:text-danger-content-strong"
                    >
                      Ver factura existente
                      <ExternalLink size={10} />
                    </Link>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-danger-surface border border-danger-border rounded-lg p-3 text-xs text-danger-content">{error}</div>
              )}

              <div>
                <label htmlFor="intl-proveedor" className={labelCls}>
                  Proveedor {!localSupplier && <span className="text-danger-solid">*</span>}
                </label>
                {readOnly ? (
                  <div className="text-sm text-content">
                    {invoice.supplier_detail?.legal_name ?? '—'}
                  </div>
                ) : (
                  <Autocomplete id="intl-proveedor"
                    items={supplierItems}
                    value={localSupplier}
                    displayValue={supplierItems.find(i => i.id === localSupplier)?.label ?? ''}
                    onChange={() => { /* query interno; la selección se confirma en onSelect */ }}
                    onSelect={(item: AutocompleteItem) => setLocalSupplier(item.id)}
                    placeholder="Buscar proveedor…"
                    className={inputCls}
                  />
                )}
                {invoice.supplier_detail && !invoice.supplier_detail.default_expense_account && (
                  <div className="mt-1 text-xs text-warning-content-soft">
                    ⚠ Sin cuentas default — se usarán las de AccountingDefaults.
                  </div>
                )}
              </div>

              {!readOnly && (
                <div>
                  <label htmlFor="intlinvoicedetailmodal-cuenta-de-gasto-opcional" className={labelCls}>Cuenta de gasto (opcional)</label>
                  <Autocomplete id="intlinvoicedetailmodal-cuenta-de-gasto-opcional"
                    items={accountItems}
                    value={localAccount}
                    displayValue={accountItems.find(i => i.id === localAccount)?.label ?? ''}
                    onChange={() => { /* selección via onSelect */ }}
                    onSelect={(item: AutocompleteItem) => setLocalAccount(item.id)}
                    placeholder="Usar cuenta del proveedor / defaults…"
                    className={inputCls}
                  />
                  {localAccount && (
                    <button
                      type="button"
                      onClick={() => setLocalAccount('')}
                      className="mt-1 text-xs text-content-muted hover:text-content underline"
                    >
                      Limpiar (volver a default)
                    </button>
                  )}
                </div>
              )}

              {!readOnly && (
                <SelectField
                  dense
                  label="Estado al importar"
                  value={localTargetStatus}
                  onChange={e => setLocalTargetStatus(e.target.value as 'approved' | 'registered')}
                  hint={'"Borrador" crea la factura de proveedor sin comprobante; se contabiliza al aprobarla después.'}
                >
                  <option value="approved">Aprobada (contabilizar ahora)</option>
                  <option value="registered">Borrador (registrada, sin contabilizar)</option>
                </SelectField>
              )}

              {!readOnly && comprasFields.length > 0 && (
                <div className="border-t border-divider pt-3">
                  <label className={labelCls}>Campos personalizados (compra)</label>
                  <CustomFieldsForm
                    fields={comprasFields}
                    value={personalizados}
                    onChange={setPersonalizados}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="intl-numero" className={labelCls}>Nº invoice</label>
                  {readOnly ? (
                    <div className="text-sm text-content font-mono">{invoice.external_number || '—'}</div>
                  ) : (
                    <input id="intl-numero" type="text" value={localNumber} onChange={e => setLocalNumber(e.target.value)} className={inputCls} />
                  )}
                </div>
                <div>
                  <label className={labelCls}>Moneda</label>
                  <div className="text-sm text-content">{invoice.currency}</div>
                </div>
                <div>
                  <label htmlFor="intl-fecha-emision" className={labelCls}>Fecha emisión</label>
                  {readOnly ? (
                    <div className="text-sm text-content">{invoice.invoice_date ?? '—'}</div>
                  ) : (
                    <DatePicker id="intl-fecha-emision" value={localInvoiceDate} onChange={setLocalInvoiceDate} />
                  )}
                </div>
                <div>
                  <label htmlFor="intl-fecha-vencimiento" className={labelCls}>Fecha vencimiento</label>
                  {readOnly ? (
                    <div className="text-sm text-content">{invoice.due_date ?? '—'}</div>
                  ) : (
                    <DatePicker id="intl-fecha-vencimiento" value={localDueDate} onChange={setLocalDueDate} />
                  )}
                </div>
                <div className="col-span-2">
                  <label htmlFor="intl-total" className={labelCls}>Total (moneda original)</label>
                  {readOnly ? (
                    <div className="text-sm text-content font-medium">
                      {fmtAmount(invoice.amount_total_original, invoice.currency)}
                    </div>
                  ) : (
                    <input id="intl-total" type="number" step="0.01" value={localTotal} onChange={e => setLocalTotal(e.target.value)} className={inputCls} />
                  )}
                </div>
              </div>

              {invoice.lines.length > 0 && (
                <div>
                  <label className={displayLabelCls}>Líneas extraídas</label>
                  <div className="mt-1 space-y-1">
                    {invoice.lines.map(line => (
                      <div key={line.id} className="flex items-center justify-between gap-3 text-xs text-content-muted bg-glass-3 rounded-lg p-2">
                        <span className="truncate">{line.description}</span>
                        <span className="font-mono shrink-0">{fmtAmount(line.amount, invoice.currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {invoice.status === 'posted' && (
                <div className="bg-success-surface border border-success-border rounded-lg p-3 text-xs text-success-content-strong">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <CheckCircle2 size={13} />
                    Contabilizada
                  </div>
                  <div className="space-y-0.5">
                    <div>Total CLP: {fmtAmount(invoice.amount_total_clp, 'CLP')}</div>
                    <div>Tasa: {invoice.exchange_rate} ({invoice.exchange_rate_date})</div>
                    {invoice.voucher && (
                      <div>
                        Voucher:{' '}
                        <Link
                          to={`/accounting/vouchers?highlight=${invoice.voucher}`}
                          onClick={onClose}
                          className="font-mono text-success-content underline decoration-dotted hover:text-emerald-900 inline-flex items-center gap-0.5"
                        >
                          {invoice.voucher_number || 'Ver'}
                          <ExternalLink size={10} />
                        </Link>
                      </div>
                    )}
                    {invoice.supplier_invoice && (
                      <div>
                        Factura proveedor:{' '}
                        <Link
                          to={`/purchases/invoices?highlight=${invoice.supplier_invoice}`}
                          onClick={onClose}
                          className="font-mono text-success-content underline decoration-dotted hover:text-emerald-900 inline-flex items-center gap-0.5"
                        >
                          {invoice.supplier_invoice_number || 'Ver'}
                          <ExternalLink size={10} />
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {invoice.status === 'error' && (
                <div className="bg-danger-surface border border-danger-border rounded-lg p-3 text-xs text-danger-content">
                  El parser falló. Usa "Re-parsear" para reintentar.
                </div>
              )}

              <div className="pt-2 border-t border-divider text-xs text-content-subtle space-y-0.5">
                <div>Origen: {invoice.source === 'email' ? `Email (${invoice.received_from_email})` : 'Upload manual'}</div>
                {invoice.received_subject && <div>Asunto: {invoice.received_subject}</div>}
                <div>PDF: {invoice.pdf_file_name}</div>
                {invoice.parse_confidence && <div>Confianza del parser: {invoice.parse_confidence}</div>}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {invoice && (
          <div className="shrink-0 flex items-center justify-between gap-2 px-5 py-3 border-t border-divider rounded-b-2xl bg-surface">
            <div className="flex items-center gap-2">
              {invoice.status !== 'posted' && invoice.status !== 'discarded' && (
                <>
                  {canEdit && (
                    <Button variant="ghost" size="sm" onClick={handleReparse} leftIcon={<RefreshCcw size={13} />} isPending={reparse.isPending}>
                      Re-parsear
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost-warning" size="sm" onClick={handleDiscard} leftIcon={<Trash2 size={13} />} isPending={discard.isPending}>
                      Eliminar
                    </Button>
                  )}
                </>
              )}
              {invoice.status === 'posted' && (
                <Badge label="Sin acciones disponibles" variant="slate" />
              )}
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && canEdit && (
                <Button variant="secondary" size="sm" onClick={handleSave} isPending={update.isPending}>
                  Guardar cambios
                </Button>
              )}
              {!readOnly && canApprove && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handlePost}
                  isPending={post.isPending}
                  disabled={!!invoice.duplicate_of}
                  title={invoice.duplicate_of ? 'Documento ya contabilizado para este proveedor' : undefined}
                >
                  Contabilizar
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}
